import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { rowId, withRowIds, rowKey, stripRowIds } from '@/lib/rowKeys';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SignaturePad from '@/components/SignaturePad';
import { emergencyPlanGaps } from '@/lib/jhaReadiness';
import { toast } from 'sonner';
import {
  Plus, FileText, Building2, MapPin, Calendar, Users, Shield,
  AlertTriangle, ClipboardCheck, HardHat, Phone, Trash2,
  Sparkles, Download, Send, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, Edit, Eye, ClipboardList, Search, Loader2, Link2, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PreConstructionJHA = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const backTarget = user?.role === 'supervisor' || user?.role === 'superintendent' || user?.role === 'service_worker'
    ? '/supervisor'
    : '/admin';
  const [jhas, setJhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvalSignature, setApprovalSignature] = useState(null);
  const [approverTitle, setApproverTitle] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [approving, setApproving] = useState(false);
  const [submitWarning, setSubmitWarning] = useState(null);
  const [approvalReadiness, setApprovalReadiness] = useState(null);
  const [useSitePlan, setUseSitePlan] = useState(false);
  const [sitePlan, setSitePlan] = useState({
    planName: '', planRevision: '', accessLocation: '', coversThisWork: false, reviewedWithCrew: false
  });
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [crewData, setCrewData] = useState(null);
  const [crewTarget, setCrewTarget] = useState(null);
  const [crewManualName, setCrewManualName] = useState('');
  const [crewSignature, setCrewSignature] = useState(null);
  const [crewSaving, setCrewSaving] = useState(false);
  const [selectedJHA, setSelectedJHA] = useState(null);
  const [activeTab, setActiveTab] = useState('project');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState('');
  const [aiError, setAiError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  
  // Library state
  const [showLibrary, setShowLibrary] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryResults, setLibraryResults] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [similarJhas, setSimilarJhas] = useState([]);
  const [findingSimilar, setFindingSimilar] = useState(false);
  
  // SDS Linking state
  const [showSdsDialog, setShowSdsDialog] = useState(false);
  const [suggestedSds, setSuggestedSds] = useState([]);
  const [linkedSds, setLinkedSds] = useState([]);
  const [sdsLoading, setSdsLoading] = useState(false);
  const [selectedSdsIds, setSelectedSdsIds] = useState(new Set());
  
  // Incidents Linking state
  const [showIncidentsDialog, setShowIncidentsDialog] = useState(false);
  const [linkedIncidents, setLinkedIncidents] = useState([]);
  const [locationIncidents, setLocationIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    projectName: '',
    projectNumber: '',
    projectLocation: '',
    projectAddress: '',
    clientName: '',
    generalContractor: '',
    estimatedStartDate: '',
    estimatedEndDate: '',
    projectDescription: '',
    scopeOfWork: '',
    siteHazards: [],
    highRiskActivities: [],
    permitRequirements: [],
    trainingRequirements: [],
    ppeRequirements: [],
    emergencyPlan: {
      nearestHospital: '',
      hospitalAddress: '',
      hospitalPhone: '',
      evacuationRoutes: [''],
      assemblyPoints: [''],
      emergencyContacts: [{ role: '', name: '', phone: '', email: '' }],
      specialProcedures: ''
    },
    subcontractors: [],
    jobSteps: []  // AI-generated task-based hazards
  });

  useEffect(() => {
    fetchJHAs();
  }, []);

  const fetchJHAs = async () => {
    try {
      const response = await axiosInstance.get('/pre-construction-jha');
      setJhas(response.data);
    } catch (error) {
      toast.error('Failed to load Pre-Construction JHAs');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      projectName: '',
      projectNumber: '',
      projectLocation: '',
      projectAddress: '',
      clientName: '',
      generalContractor: '',
      estimatedStartDate: '',
      estimatedEndDate: '',
      projectDescription: '',
      scopeOfWork: '',
      siteHazards: [],
      highRiskActivities: [],
      permitRequirements: [],
      trainingRequirements: [],
      ppeRequirements: [],
      emergencyPlan: {
        nearestHospital: '',
        hospitalAddress: '',
        hospitalPhone: '',
        evacuationRoutes: [''],
        assemblyPoints: [''],
        emergencyContacts: [{ role: '', name: '', phone: '', email: '' }],
        specialProcedures: ''
      },
      subcontractors: [],
      jobSteps: []
    });
    setSelectedJHA(null);
    setActiveTab('project');
    setSimilarJhas([]);
    setAiAnalysis(null);
    setQualityWarnings([]);
    setJobSteps([]);
  };

  // Library Functions
  const searchLibrary = async (searchTerm = '') => {
    setLibraryLoading(true);
    try {
      const response = await axiosInstance.get('/pre-construction-jha/library/search', {
        params: { q: searchTerm, limit: 20 }
      });
      setLibraryResults(response.data.results || []);
    } catch (error) {
      toast.error('Failed to search JHA library');
    } finally {
      setLibraryLoading(false);
    }
  };

  const findSimilarJhas = async () => {
    if (!formData.projectDescription && !formData.scopeOfWork) {
      toast.error('Please enter project description or scope of work first');
      return;
    }
    
    setFindingSimilar(true);
    setSimilarJhas([]);
    try {
      const response = await axiosInstance.post('/pre-construction-jha/library/find-similar', {
        projectDescription: formData.projectDescription,
        scopeOfWork: formData.scopeOfWork
      });
      
      const results = response.data.similarJhas || [];
      setSimilarJhas(results);
      
      if (results.length > 0) {
        toast.success(`Found ${results.length} similar JHAs in your library!`);
      } else {
        toast.info(response.data.message || 'No similar JHAs found');
      }
    } catch (error) {
      toast.error('Failed to find similar JHAs');
    } finally {
      setFindingSimilar(false);
    }
  };

  // SDS Linking Functions
  const fetchSuggestedSds = async (jhaId) => {
    setSdsLoading(true);
    try {
      const response = await axiosInstance.get(`/pre-construction-jha/${jhaId}/suggest-sds`);
      setSuggestedSds(response.data.suggestedSds || []);
      
      // Also get already linked SDS
      const linkedResponse = await axiosInstance.get(`/pre-construction-jha/${jhaId}/linked-sds`);
      setLinkedSds(linkedResponse.data.linkedSds || []);
      setSelectedSdsIds(new Set(linkedResponse.data.linkedSds?.map(s => s.id) || []));
    } catch (error) {
      console.error('Failed to fetch SDS suggestions:', error);
    } finally {
      setSdsLoading(false);
    }
  };

  const handleLinkSds = async (jhaId) => {
    if (selectedSdsIds.size === 0) {
      toast.warning('Select at least one SDS to link');
      return;
    }
    
    try {
      await axiosInstance.post(`/pre-construction-jha/${jhaId}/link-sds`, {
        sds_ids: Array.from(selectedSdsIds)
      });
      toast.success(`Linked ${selectedSdsIds.size} SDS document(s) to JHA`);
      setShowSdsDialog(false);
      fetchJHAs();
    } catch (error) {
      toast.error('Failed to link SDS documents');
    }
  };

  // Incidents Linking Functions
  const fetchLinkedIncidents = async (jhaId) => {
    setIncidentsLoading(true);
    try {
      const response = await axiosInstance.get(`/pre-construction-jha/${jhaId}/linked-incidents`);
      setLinkedIncidents(response.data.linkedIncidents || []);
      setLocationIncidents(response.data.locationIncidents || []);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    } finally {
      setIncidentsLoading(false);
    }
  };

  // Sync to Library Function
  const handleSyncToLibrary = async (jhaId) => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post(`/pre-construction-jha/${jhaId}/sync-to-library`);
      const { synced, updated, message } = response.data;
      
      if (synced > 0 || updated > 0) {
        toast.success(`Library updated! ${synced} new tasks added, ${updated} tasks updated.`);
      } else {
        toast.info(message || 'Library already up to date');
      }
      fetchJHAs();
    } catch (error) {
      toast.error('Failed to sync to library');
    } finally {
      setSyncing(false);
    }
  };

  const applyAsTemplate = async (templateId) => {
    try {
      const response = await axiosInstance.post(`/pre-construction-jha/library/use-template/${templateId}`);
      const templateData = response.data.templateData;
      
      // Merge template data with current form
      setFormData(prev => ({
        ...prev,
        ...templateData,
        projectName: prev.projectName || templateData.projectName,
        projectLocation: prev.projectLocation || '',
        projectDescription: prev.projectDescription || templateData.projectDescription,
        scopeOfWork: prev.scopeOfWork || templateData.scopeOfWork
      }));
      
      toast.success(`Template loaded! ${response.data.originalStats.hazardCount} hazards, ${response.data.originalStats.activityCount} activities copied.`);
      setSimilarJhas([]);
      setShowLibrary(false);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (jha) => {
    setSelectedJHA(jha);
    setFormData({
      ...jha,
      siteHazards: withRowIds(jha.siteHazards || []),
      highRiskActivities: withRowIds(jha.highRiskActivities || []),
      permitRequirements: withRowIds(jha.permitRequirements || []),
      trainingRequirements: withRowIds(jha.trainingRequirements || []),
      ppeRequirements: withRowIds(jha.ppeRequirements || []),
      subcontractors: withRowIds(jha.subcontractors || []),
      emergencyPlan: jha.emergencyPlan
        ? { ...jha.emergencyPlan, emergencyContacts: withRowIds(jha.emergencyPlan.emergencyContacts || []) }
        : {
        nearestHospital: '',
        hospitalAddress: '',
        hospitalPhone: '',
        evacuationRoutes: [''],
        assemblyPoints: [''],
        emergencyContacts: [{ _rowId: rowId(), role: '', name: '', phone: '', email: '' }],
        specialProcedures: ''
      },
      jobSteps: jha.jobSteps || []
    });
    // Also set the jobSteps state for display
    setJobSteps(jha.jobSteps || []);
    setShowDialog(true);
  };

  const handleView = (jha) => {
    setSelectedJHA(jha);
    setShowViewDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = stripRowIds(formData);
      if (selectedJHA) {
        await axiosInstance.put(`/pre-construction-jha/${selectedJHA.id}`, payload);
        toast.success('Pre-Construction JHA updated');
      } else {
        await axiosInstance.post('/pre-construction-jha', payload);
        toast.success('Pre-Construction JHA created');
      }
      setShowDialog(false);
      resetForm();
      fetchJHAs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleSubmit = async (jhaId, { skipWarning = false } = {}) => {
    const jha = jhas.find(j => j.id === jhaId);
    const gaps = emergencyPlanGaps(jha);
    if (!skipWarning && gaps.length > 0) {
      setSubmitWarning({ jha, gaps });
      return;
    }
    try {
      await axiosInstance.post(`/pre-construction-jha/${jhaId}/submit`);
      toast.success('JHA submitted for approval');
      setSubmitWarning(null);
      fetchJHAs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const openApproveDialog = async (jha) => {
    setSelectedJHA(jha);
    setApprovalReadiness(null);
    setUseSitePlan(false);
    setSitePlan({ planName: '', planRevision: '', accessLocation: '', coversThisWork: false, reviewedWithCrew: false });
    setShowApproveDialog(true);
    try {
      const res = await axiosInstance.get(`/pre-construction-jha/${jha.id}/readiness`);
      setApprovalReadiness(res.data);
    } catch (error) {
      toast.error('Could not check emergency plan status');
    }
  };

  const sitePlanComplete = Boolean(
    sitePlan.planName.trim() && sitePlan.accessLocation.trim() && sitePlan.coversThisWork && sitePlan.reviewedWithCrew
  );
  const emergencyBlocked = approvalReadiness ? !approvalReadiness.canApprove : false;
  const canApprove = Boolean(approvalSignature) && (!emergencyBlocked || (useSitePlan && sitePlanComplete));

  const handleApprove = async (jhaId) => {
    if (!approvalSignature) {
      toast.error('Please sign before approving');
      return;
    }
    setApproving(true);
    try {
      await axiosInstance.post(`/pre-construction-jha/${jhaId}/approve`, {
        signature: approvalSignature,
        signerTitle: approverTitle,
        comments: approvalComments,
        ...(useSitePlan ? { siteEmergencyPlan: { applies: true, ...sitePlan } } : {})
      });
      toast.success('JHA approved and signed — crew sign-on is now open');
      setShowApproveDialog(false);
      setApprovalSignature(null);
      setApproverTitle('');
      setApprovalComments('');
      fetchJHAs();
      setShowViewDialog(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const openCrewDialog = async (jha) => {
    setSelectedJHA(jha);
    setCrewData(null);
    setCrewTarget(null);
    setCrewManualName('');
    setCrewSignature(null);
    setShowCrewDialog(true);
    try {
      const res = await axiosInstance.get(`/pre-construction-jha/${jha.id}/crew-signons`);
      setCrewData(res.data);
    } catch (error) {
      toast.error('Could not load crew sign-on status');
    }
  };

  const handleCrewSignOn = async () => {
    if (!crewSignature || !selectedJHA) return;
    setCrewSaving(true);
    try {
      const res = await axiosInstance.post(`/pre-construction-jha/${selectedJHA.id}/crew-signon`, {
        ...(crewTarget?.id
          ? { workerId: crewTarget.id }
          : { workerName: (crewTarget?.name || crewManualName).trim() }),
        signature: crewSignature
      });
      toast.success(res.data?.message || 'Signature recorded');
      setCrewTarget(null);
      setCrewManualName('');
      setCrewSignature(null);
      const refreshed = await axiosInstance.get(`/pre-construction-jha/${selectedJHA.id}/crew-signons`);
      setCrewData(refreshed.data);
      fetchJHAs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record the signature');
    } finally {
      setCrewSaving(false);
    }
  };

  const handleReject = async (jhaId, reason) => {
    try {
      await axiosInstance.post(`/pre-construction-jha/${jhaId}/reject`, { reason });
      toast.success('JHA rejected');
      fetchJHAs();
      setShowViewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleDelete = async (jhaId) => {
    if (!confirm('Are you sure you want to delete this JHA?')) return;
    try {
      await axiosInstance.delete(`/pre-construction-jha/${jhaId}`);
      toast.success('JHA deleted');
      fetchJHAs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDownloadPDF = async (jhaId) => {
    try {
      const response = await axiosInstance.get(`/pre-construction-jha/${jhaId}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pre_construction_jha_${jhaId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // State for AI analysis results
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [qualityWarnings, setQualityWarnings] = useState([]);
  const [jobSteps, setJobSteps] = useState([]);

  // ---- Background JHA generation: survives leaving, refreshing or reopening the app ----
  const JHA_JOB_KEY = 'jha_ai_job_id';

  const applyAISuggestions = (suggestions) => {
    setAiAnalysis({ parsedScope: suggestions.parsedScope, metadata: suggestions.metadata });

    // Merge job steps by task so the user's own edits and added steps survive a retry
    if (suggestions.jobSteps && suggestions.jobSteps.length > 0) {
      setFormData(prev => {
        const existing = prev.jobSteps || [];
        const byTask = new Map(existing.map(s => [(s.task || '').toLowerCase(), s]));
        suggestions.jobSteps.forEach(s => {
          const key = (s.task || '').toLowerCase();
          if (!byTask.has(key)) byTask.set(key, s);
        });
        const merged = Array.from(byTask.values());
        setJobSteps(merged);
        return { ...prev, jobSteps: merged };
      });
    }

    if (suggestions.qualityWarnings && suggestions.qualityWarnings.length > 0) {
      setQualityWarnings(suggestions.qualityWarnings);
    }

    setFormData(prev => {
      const existingHazardDescs = new Set(prev.siteHazards.map(h => h.description?.toLowerCase()));
      const existingActivityNames = new Set(prev.highRiskActivities.map(a => a.activity?.toLowerCase()));
      const existingTrainingTypes = new Set(prev.trainingRequirements.map(t => t.trainingType?.toLowerCase()));
      const existingPPEAreas = new Set(prev.ppeRequirements.map(p => p.workArea?.toLowerCase()));
      const existingPermitTypes = new Set(prev.permitRequirements.map(p => p.permitType?.toLowerCase()));

      return {
        ...prev,
        siteHazards: withRowIds([
          ...prev.siteHazards,
          ...(suggestions.siteHazards || []).filter(h => !existingHazardDescs.has(h.description?.toLowerCase()))
        ]),
        highRiskActivities: withRowIds([
          ...prev.highRiskActivities,
          ...(suggestions.highRiskActivities || []).filter(a => !existingActivityNames.has(a.activity?.toLowerCase()))
        ]),
        trainingRequirements: withRowIds([
          ...prev.trainingRequirements,
          ...(suggestions.trainingRequirements || []).filter(t => !existingTrainingTypes.has(t.trainingType?.toLowerCase()))
        ]),
        ppeRequirements: withRowIds([
          ...prev.ppeRequirements,
          ...(suggestions.ppeRequirements || []).filter(p => !existingPPEAreas.has(p.workArea?.toLowerCase()))
        ]),
        permitRequirements: withRowIds([
          ...prev.permitRequirements,
          ...(suggestions.permitRequirements || []).filter(p => !existingPermitTypes.has(p.permitType?.toLowerCase()))
        ])
      };
    });

    const stepsCount = suggestions.jobSteps?.length || 0;
    const dropped = suggestions.metadata?.schemaIssues?.length || 0;
    if (dropped > 0) {
      toast.warning(`Draft ready: ${stepsCount} job steps. ${dropped} incomplete row(s) were discarded by the schema check.`, { duration: 7000 });
    } else {
      toast.success(`Draft ready! ${stepsCount} job steps with task-specific hazards. Review, edit and accept.`);
    }
  };

  const pollAIJob = async (jobId) => {
    localStorage.setItem(JHA_JOB_KEY, jobId);
    setAiLoading(true);
    setAiError('');
    try {
      for (let i = 0; i < 200; i++) {
        const { data } = await axiosInstance.get(`/ai-jobs/${jobId}`);
        setAiProgress(data.progress || data.status);
        if (data.status === 'completed') {
          applyAISuggestions(data.result || {});
          localStorage.removeItem(JHA_JOB_KEY);
          localStorage.removeItem('jha_ai_job_payload');
          return;
        }
        if (data.status === 'failed') {
          setAiError(data.error || 'Generation failed. Your inputs were kept — tap retry.');
          toast.error(data.error || 'Generation failed. Your inputs were kept — tap retry.');
          localStorage.removeItem(JHA_JOB_KEY);
          return;
        }
        await new Promise(r => setTimeout(r, 4000));
      }
      setAiError('Still generating. You can leave this screen — reopen to recover the draft.');
    } catch (error) {
      if (error.response?.status === 404) localStorage.removeItem(JHA_JOB_KEY);
      setAiError(error.response?.data?.detail || error.message || 'Could not read the generation job');
    } finally {
      setAiLoading(false);
      setAiProgress('');
    }
  };

  // Recover a generation that was still running when the user left or refreshed
  useEffect(() => {
    const saved = localStorage.getItem(JHA_JOB_KEY);
    if (saved && showDialog) {
      // Restore the inputs the draft is being generated from so nothing is lost
      try {
        const kept = JSON.parse(localStorage.getItem('jha_ai_job_payload') || 'null');
        if (kept) {
          setFormData(prev => ({
            ...prev,
            projectDescription: prev.projectDescription || kept.projectDescription || '',
            scopeOfWork: prev.scopeOfWork || kept.scopeOfWork || '',
            projectLocation: prev.projectLocation || kept.projectLocation || '',
          }));
        }
      } catch (e) { /* ignore */ }
      toast.info('Recovering your JHA draft that was still generating…');
      pollAIJob(saved);
    }
  }, [showDialog]);

  const getAISuggestions = async () => {
    if (!formData.projectDescription || !formData.scopeOfWork) {
      toast.error('Please fill in Project Description and Scope of Work first');
      return;
    }

    setAiError('');
    setQualityWarnings([]);
    setAiLoading(true);
    setAiProgress('Analyzing scope against your library, manual and SOPs');
    localStorage.setItem('jha_ai_job_payload', JSON.stringify({
      projectDescription: formData.projectDescription,
      scopeOfWork: formData.scopeOfWork,
      projectLocation: formData.projectLocation,
    }));
    try {
      const { data } = await axiosInstance.post('/pre-construction-jha/ai-suggestions-async', {
        projectDescription: formData.projectDescription,
        scopeOfWork: formData.scopeOfWork,
        projectLocation: formData.projectLocation
      });
      if (data.reused) toast.info('Already generating this scope — reconnecting to that job');
      await pollAIJob(data.jobId);
    } catch (error) {
      console.error('AI suggestions error:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to start generation';
      setAiError(msg);
      toast.error(`Failed to start AI generation: ${msg}`);
      setAiLoading(false);
      setAiProgress('');
    }
  };


  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      submitted: { color: 'bg-yellow-100 text-yellow-800', icon: Send },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config.draft;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Helper functions for adding/removing array items
  const addArrayItem = (field, template) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], { ...template, _rowId: rowId() }]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateArrayItem = (field, index, updates) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? { ...item, ...updates } : item)
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backTarget)}
            className="mb-2 -ml-2"
            data-testid="pre-construction-jha-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Pre-Construction JHA</h1>
          <p className="text-gray-600">Job Hazard Analysis for project planning</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { setShowLibrary(true); searchLibrary(); }}
            className="gap-2"
          >
            <ClipboardList className="w-4 h-4" />
            JHA Library
          </Button>
          <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            New Pre-Construction JHA
          </Button>
        </div>
      </div>

      {/* JHA Library Dialog */}
      <Dialog open={showLibrary} onOpenChange={setShowLibrary}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              JHA Library
            </DialogTitle>
            <DialogDescription>
              Browse completed JHAs and use them as templates for new projects
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by project name, description, or scope..."
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchLibrary(librarySearch)}
                className="flex-1"
              />
              <Button onClick={() => searchLibrary(librarySearch)} disabled={libraryLoading}>
                {libraryLoading ? 'Searching...' : 'Search'}
              </Button>
            </div>
            
            {/* Results */}
            <div className="space-y-3">
              {libraryResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p>No JHAs in library yet</p>
                  <p className="text-sm">Complete and approve JHAs to build your library</p>
                </div>
              ) : (
                libraryResults.map(jha => (
                  <Card key={jha.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{jha.projectName}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2">{jha.projectDescription}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>📍 {jha.projectLocation}</span>
                          <span>⚠️ {jha.stats?.hazardCount || 0} hazards</span>
                          <span>📋 {jha.stats?.activityCount || 0} activities</span>
                          <span>📝 {jha.stats?.permitCount || 0} permits</span>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          applyAsTemplate(jha.id);
                          setShowDialog(true);
                        }}
                      >
                        Use Template
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* JHA List */}
      <div className="grid gap-4">
        {jhas.length === 0 ? (
          <Card className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pre-Construction JHAs</h3>
            <p className="text-gray-600 mb-4">Create your first project-level job hazard analysis</p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Create JHA
            </Button>
          </Card>
        ) : (
          jhas.map(jha => (
            <Card key={jha.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{jha.projectName}</h3>
                          {getStatusBadge(jha.status)}
                        </div>
                        {jha.projectNumber && (
                          <p className="text-sm text-gray-500">#{jha.projectNumber}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {jha.projectLocation}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {jha.estimatedStartDate} - {jha.estimatedEndDate}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {jha.clientName}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{jha.projectDescription}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => handleView(jha)}>
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {jha.status === 'draft' && (
                      <>
                        {emergencyPlanGaps(jha).length > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-1"
                            data-testid={`emergency-plan-incomplete-${jha.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Emergency plan incomplete
                          </span>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handleEdit(jha)}>
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" onClick={() => handleSubmit(jha.id)} className="bg-yellow-600 hover:bg-yellow-700" data-testid={`submit-jha-${jha.id}`}>
                          <Send className="w-4 h-4 mr-1" />
                          Submit
                        </Button>
                      </>
                    )}
                    {jha.status === 'rejected' && (
                      <Button variant="outline" size="sm" onClick={() => handleEdit(jha)}>
                        <Edit className="w-4 h-4 mr-1" />
                        Revise
                      </Button>
                    )}
                    {jha.status === 'approved' && (
                      <>
                        {jha.crewSignOnSummary?.reacknowledgmentRequired && (
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-1"
                            data-testid={`reacknowledgment-required-${jha.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Crew must re-sign rev {jha.crewSignOnSummary.revision}
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCrewDialog(jha)}
                          className="text-indigo-600 hover:text-indigo-700"
                          data-testid={`crew-signon-btn-${jha.id}`}
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Crew Sign-On{jha.crewSignOnSummary ? ` (${jha.crewSignOnSummary.signed})` : ''}
                        </Button>
                      </>
                    )}
                    {/* SDS Linking Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setSelectedJHA(jha);
                        fetchSuggestedSds(jha.id);
                        setShowSdsDialog(true);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      SDS {jha.linkedSdsDocuments?.length > 0 && `(${jha.linkedSdsDocuments.length})`}
                    </Button>
                    {/* Incidents Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setSelectedJHA(jha);
                        fetchLinkedIncidents(jha.id);
                        setShowIncidentsDialog(true);
                      }}
                      className="text-orange-600 hover:text-orange-700"
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Incidents
                    </Button>
                    {/* Sync to Library Button - only for approved JHAs */}
                    {jha.status === 'approved' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSyncToLibrary(jha.id)}
                        disabled={syncing}
                        className={`${jha.syncedToLibrary ? 'text-green-600' : 'text-purple-600'} hover:text-purple-700`}
                      >
                        {syncing ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : jha.syncedToLibrary ? (
                          <CheckCircle className="w-4 h-4 mr-1" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-1" />
                        )}
                        {jha.syncedToLibrary ? 'Synced' : 'Sync Library'}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(jha.id)}>
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(jha.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJHA ? 'Edit' : 'Create'} Pre-Construction JHA</DialogTitle>
            <DialogDescription>
              Complete the job hazard analysis for your construction project
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 lg:grid-cols-8 gap-1">
              <TabsTrigger value="project" className="text-xs">Project</TabsTrigger>
              <TabsTrigger value="hazards" className="text-xs">Hazards</TabsTrigger>
              <TabsTrigger value="activities" className="text-xs">Activities</TabsTrigger>
              <TabsTrigger value="permits" className="text-xs">Permits</TabsTrigger>
              <TabsTrigger value="training" className="text-xs">Training</TabsTrigger>
              <TabsTrigger value="ppe" className="text-xs">PPE</TabsTrigger>
              <TabsTrigger value="emergency" className="text-xs">Emergency</TabsTrigger>
              <TabsTrigger value="subs" className="text-xs">Subs</TabsTrigger>
            </TabsList>

            {/* Project Information Tab */}
            <TabsContent value="project" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input
                    value={formData.projectName}
                    onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                    placeholder="Enter project name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project Number</Label>
                  <Input
                    value={formData.projectNumber}
                    onChange={(e) => setFormData({ ...formData, projectNumber: e.target.value })}
                    placeholder="e.g., PRJ-2024-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project Location *</Label>
                  <Input
                    value={formData.projectLocation}
                    onChange={(e) => setFormData({ ...formData, projectLocation: e.target.value })}
                    placeholder="City, State"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project Address *</Label>
                  <Input
                    value={formData.projectAddress}
                    onChange={(e) => setFormData({ ...formData, projectAddress: e.target.value })}
                    placeholder="Full street address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    placeholder="Client or owner name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>General Contractor *</Label>
                  <Input
                    value={formData.generalContractor}
                    onChange={(e) => setFormData({ ...formData, generalContractor: e.target.value })}
                    placeholder="GC company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estimated Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.estimatedStartDate}
                    onChange={(e) => setFormData({ ...formData, estimatedStartDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estimated End Date *</Label>
                  <Input
                    type="date"
                    value={formData.estimatedEndDate}
                    onChange={(e) => setFormData({ ...formData, estimatedEndDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Project Description *</Label>
                <Textarea
                  value={formData.projectDescription}
                  onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
                  placeholder="Describe the overall project..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Scope of Work *</Label>
                <Textarea
                  value={formData.scopeOfWork}
                  onChange={(e) => setFormData({ ...formData, scopeOfWork: e.target.value })}
                  placeholder="Detail the work to be performed... (Include specific trades, equipment, work environments, and high-risk activities)"
                  rows={4}
                />
              </div>
              
              {/* Find Similar JHAs from Library */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-900 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Find Similar JHAs
                    </h4>
                    <p className="text-sm text-blue-700">
                      Search your library for similar past projects to use as templates
                    </p>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={findSimilarJhas}
                    disabled={findingSimilar || (!formData.projectDescription && !formData.scopeOfWork)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {findingSimilar ? (
                      <>
                        <span className="animate-spin mr-2">🔍</span>
                        Searching...
                      </>
                    ) : (
                      <>
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Find Similar
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Similar JHAs Results */}
                {similarJhas.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-blue-800 font-medium">Found {similarJhas.length} similar JHAs:</p>
                    {similarJhas.map(jha => (
                      <div key={jha.id} className="bg-white border border-blue-200 rounded-lg p-3 flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 truncate">{jha.projectName}</h5>
                          <p className="text-xs text-gray-600 line-clamp-1">{jha.projectDescription}</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {jha.matchedElements?.trades?.map((t, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-blue-50">🔧 {t}</Badge>
                            ))}
                            {jha.matchedElements?.highRiskOperations?.map((r, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700">⚠️ {r}</Badge>
                            ))}
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            <span>{jha.stats?.hazardCount || 0} hazards</span>
                            <span>{jha.stats?.activityCount || 0} activities</span>
                            <span>{jha.stats?.permitCount || 0} permits</span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-shrink-0"
                          onClick={() => applyAsTemplate(jha.id)}
                        >
                          Use
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* AI Suggestions Button */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-purple-900 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      AI-Powered Safety Analysis
                    </h4>
                    <p className="text-sm text-purple-700">
                      AI will parse your scope, identify hazards, and generate linked activities, training, PPE, and permits
                    </p>
                  </div>
                  <Button 
                    onClick={getAISuggestions} 
                    disabled={aiLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="jha-generate-btn"
                  >
                    {aiLoading ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        {aiProgress || 'Analyzing Scope...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analyze &amp; Generate
                      </>
                    )}
                  </Button>
                </div>
                {aiLoading && (
                  <p className="text-xs text-purple-700" data-testid="jha-generating-note">
                    This runs in the background — you can close this screen, refresh, or come back later
                    and the draft will be waiting.
                  </p>
                )}
                {aiError && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800"
                       data-testid="jha-generate-error">
                    {aiError}
                    <Button type="button" size="sm" variant="outline" className="ml-2 h-6"
                            onClick={getAISuggestions} data-testid="jha-generate-retry-btn">
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Hazards Tab - Job Steps with Task-Based Hazards */}
            <TabsContent value="hazards" className="space-y-4 mt-4">
              {/* Job Steps Section */}
              <div className="flex justify-between items-center">
                <h3 className="font-medium flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Job Steps & Hazards Analysis
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const newStep = {
                      stepNumber: jobSteps.length + 1,
                      task: '',
                      hazards: [{
                        hazardType: '',
                        description: '',
                        controlMeasures: '',
                        riskLevel: 'Medium'
                      }]
                    };
                    setJobSteps([...jobSteps, newStep]);
                    setFormData(prev => ({ ...prev, jobSteps: [...jobSteps, newStep] }));
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Task
                </Button>
              </div>
              
              {jobSteps.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No job steps yet.</p>
                  <p className="text-sm">Use "Analyze & Generate" on the Project tab, or add tasks manually.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobSteps.map((step, stepIdx) => (
                    <Card key={stepIdx} className="p-4 border-l-4 border-l-blue-500">
                      {/* Task Header */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                          {step.stepNumber || stepIdx + 1}
                        </div>
                        <div className="flex-1">
                          <Input
                            value={step.task || ''}
                            onChange={(e) => {
                              const updated = [...jobSteps];
                              updated[stepIdx] = { ...step, task: e.target.value };
                              setJobSteps(updated);
                              setFormData(prev => ({ ...prev, jobSteps: updated }));
                            }}
                            placeholder="Enter task description (e.g., Excavation for underground utilities)"
                            className="font-medium"
                          />
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            const updated = jobSteps.filter((_, i) => i !== stepIdx);
                            setJobSteps(updated);
                            setFormData(prev => ({ ...prev, jobSteps: updated }));
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                      
                      {/* Hazards for this task */}
                      <div className="ml-12 space-y-3">
                        <div className="flex justify-between items-center">
                          <Label className="text-sm font-medium text-gray-700">
                            Hazards ({step.hazards?.length || 0})
                          </Label>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              const updated = [...jobSteps];
                              const newHazard = {
                                hazardType: '',
                                description: '',
                                controlMeasures: '',
                                riskLevel: 'Medium'
                              };
                              updated[stepIdx] = {
                                ...step,
                                hazards: [...(step.hazards || []), newHazard]
                              };
                              setJobSteps(updated);
                              setFormData(prev => ({ ...prev, jobSteps: updated }));
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Hazard
                          </Button>
                        </div>
                        
                        {(step.hazards || []).map((hazard, hazardIdx) => (
                          <div key={hazardIdx} className="border rounded-lg p-3 bg-gray-50 space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-xs ${
                                  hazard.riskLevel === 'Critical' ? 'bg-red-100 text-red-700 border-red-300' :
                                  hazard.riskLevel === 'High' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                  hazard.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                  'bg-green-100 text-green-700 border-green-300'
                                }`}>
                                  {hazard.riskLevel || 'Medium'}
                                </Badge>
                                <span className="text-sm text-gray-500">Hazard {hazardIdx + 1}</span>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const updated = [...jobSteps];
                                  updated[stepIdx] = {
                                    ...step,
                                    hazards: step.hazards.filter((_, i) => i !== hazardIdx)
                                  };
                                  setJobSteps(updated);
                                  setFormData(prev => ({ ...prev, jobSteps: updated }));
                                }}
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Hazard Type</Label>
                                <Input
                                  value={hazard.hazardType || ''}
                                  onChange={(e) => {
                                    const updated = [...jobSteps];
                                    updated[stepIdx].hazards[hazardIdx] = {
                                      ...hazard,
                                      hazardType: e.target.value
                                    };
                                    setJobSteps(updated);
                                    setFormData(prev => ({ ...prev, jobSteps: updated }));
                                  }}
                                  placeholder="e.g., Cave-in, Fall, Struck-by"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Risk Level</Label>
                                <Select
                                  value={hazard.riskLevel || 'Medium'}
                                  onValueChange={(v) => {
                                    const updated = [...jobSteps];
                                    updated[stepIdx].hazards[hazardIdx] = {
                                      ...hazard,
                                      riskLevel: v
                                    };
                                    setJobSteps(updated);
                                    setFormData(prev => ({ ...prev, jobSteps: updated }));
                                  }}
                                >
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Low">🟢 Low</SelectItem>
                                    <SelectItem value="Medium">🟡 Medium</SelectItem>
                                    <SelectItem value="High">🟠 High</SelectItem>
                                    <SelectItem value="Critical">🔴 Critical</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1 md:col-span-2">
                                <Label className="text-xs">Description</Label>
                                <Textarea
                                  value={hazard.description || ''}
                                  onChange={(e) => {
                                    const updated = [...jobSteps];
                                    updated[stepIdx].hazards[hazardIdx] = {
                                      ...hazard,
                                      description: e.target.value
                                    };
                                    setJobSteps(updated);
                                    setFormData(prev => ({ ...prev, jobSteps: updated }));
                                  }}
                                  placeholder="Describe the specific hazard"
                                  rows={2}
                                />
                              </div>
                              <div className="space-y-1 md:col-span-2">
                                <Label className="text-xs">Control Measures</Label>
                                <Textarea
                                  value={hazard.controlMeasures || ''}
                                  onChange={(e) => {
                                    const updated = [...jobSteps];
                                    updated[stepIdx].hazards[hazardIdx] = {
                                      ...hazard,
                                      controlMeasures: e.target.value
                                    };
                                    setJobSteps(updated);
                                    setFormData(prev => ({ ...prev, jobSteps: updated }));
                                  }}
                                  placeholder="Specific controls to mitigate this hazard"
                                  rows={2}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {(!step.hazards || step.hazards.length === 0) && (
                          <div className="text-center py-4 text-gray-400 text-sm border border-dashed rounded">
                            No hazards for this task. Click "Add Hazard" above.
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* High-Risk Activities Tab */}
            <TabsContent value="activities" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">High-Risk Activities</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => addArrayItem('highRiskActivities', {
                    activity: '',
                    description: '',
                    permitRequired: false,
                    permitType: '',
                    specialTraining: [],
                    controlMeasures: '',
                    responsibleParty: '',
                    riskLevel: 'High',
                    rationale: ''
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Activity
                </Button>
              </div>
              
              {formData.highRiskActivities.map((activity, index) => (
                <Card key={rowKey(activity, index, 'activity')} className={`p-4 ${activity.rationale ? 'border-l-4 border-l-purple-400' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={
                        activity.riskLevel === 'Critical' ? 'bg-red-100 text-red-700 border-red-300' :
                        activity.riskLevel === 'High' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                        'bg-yellow-100 text-yellow-700 border-yellow-300'
                      }>
                        {activity.riskLevel || 'High'} Risk Activity
                      </Badge>
                      {activity.permitRequired && (
                        <Badge className="bg-red-600 text-white">Permit Required</Badge>
                      )}
                      {activity.rationale && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          AI
                        </Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeArrayItem('highRiskActivities', index)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  
                  {/* AI Rationale */}
                  {activity.rationale && (
                    <div className="mb-3 p-2 bg-purple-50 rounded-md text-xs text-purple-800">
                      <span className="font-medium">💡 Why added:</span> {activity.rationale}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Activity</Label>
                      <Select
                        value={activity.activity}
                        onValueChange={(v) => updateArrayItem('highRiskActivities', index, { activity: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select activity" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Excavation">Excavation</SelectItem>
                          <SelectItem value="Confined Space Entry">Confined Space Entry</SelectItem>
                          <SelectItem value="Hot Work">Hot Work</SelectItem>
                          <SelectItem value="Steel Erection">Steel Erection</SelectItem>
                          <SelectItem value="Crane Operations">Crane Operations</SelectItem>
                          <SelectItem value="Electrical Work">Electrical Work</SelectItem>
                          <SelectItem value="Demolition">Demolition</SelectItem>
                          <SelectItem value="Working at Heights">Working at Heights</SelectItem>
                          <SelectItem value="Hazardous Materials">Hazardous Materials</SelectItem>
                          <SelectItem value="Underground Utilities">Underground Utilities</SelectItem>
                          <SelectItem value="Gas Piping">Gas Piping</SelectItem>
                          <SelectItem value="Rooftop Work">Rooftop Work</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Responsible Party</Label>
                      <Input
                        value={activity.responsibleParty}
                        onChange={(e) => updateArrayItem('highRiskActivities', index, { responsibleParty: e.target.value })}
                        placeholder="Who is responsible"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Description</Label>
                      <Textarea
                        value={activity.description}
                        onChange={(e) => updateArrayItem('highRiskActivities', index, { description: e.target.value })}
                        placeholder="Detailed description of the activity and associated risks"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={activity.permitRequired}
                          onChange={(e) => updateArrayItem('highRiskActivities', index, { permitRequired: e.target.checked })}
                          className="rounded"
                        />
                        Permit Required
                      </Label>
                      {activity.permitRequired && (
                        <Input
                          value={activity.permitType}
                          onChange={(e) => updateArrayItem('highRiskActivities', index, { permitType: e.target.value })}
                          placeholder="Permit type"
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Special Training Required</Label>
                      <Input
                        value={activity.specialTraining?.join(', ') || ''}
                        onChange={(e) => updateArrayItem('highRiskActivities', index, { 
                          specialTraining: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        placeholder="e.g., OSHA 30, Competent Person"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Control Measures</Label>
                      <Textarea
                        value={activity.controlMeasures}
                        onChange={(e) => updateArrayItem('highRiskActivities', index, { controlMeasures: e.target.value })}
                        placeholder="Safety controls and precautions"
                        rows={2}
                      />
                    </div>
                  </div>
                </Card>
              ))}
              
              {formData.highRiskActivities.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No high-risk activities added yet.</p>
                </div>
              )}
            </TabsContent>

            {/* Permits Tab */}
            <TabsContent value="permits" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Permit Requirements</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => addArrayItem('permitRequirements', {
                    permitType: '',
                    requiredFor: '',
                    issuingAuthority: '',
                    status: 'Pending',
                    expirationDate: '',
                    notes: '',
                    rationale: ''
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Permit
                </Button>
              </div>
              
              {formData.permitRequirements.map((permit, index) => (
                <Card key={rowKey(permit, index, 'permit')} className={`p-4 ${permit.rationale ? 'border-l-4 border-l-purple-400' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={
                        permit.status === 'Obtained' ? 'bg-green-50 text-green-700' :
                        permit.status === 'Pending' ? 'bg-yellow-50 text-yellow-700' :
                        'bg-gray-50 text-gray-700'
                      }>
                        <ClipboardCheck className="w-3 h-3 mr-1" />
                        {permit.permitType || `Permit #${index + 1}`}
                      </Badge>
                      <Badge variant="outline" className={
                        permit.status === 'Obtained' ? 'bg-green-100 text-green-700' :
                        permit.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {permit.status}
                      </Badge>
                      {permit.rationale && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          AI
                        </Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeArrayItem('permitRequirements', index)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  
                  {/* AI Rationale */}
                  {permit.rationale && (
                    <div className="mb-3 p-2 bg-purple-50 rounded-md text-xs text-purple-800">
                      <span className="font-medium">💡 Why required:</span> {permit.rationale}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Permit Type</Label>
                      <Input
                        value={permit.permitType}
                        onChange={(e) => updateArrayItem('permitRequirements', index, { permitType: e.target.value })}
                        placeholder="e.g., Hot Work Permit, Excavation Permit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Required For</Label>
                      <Input
                        value={permit.requiredFor}
                        onChange={(e) => updateArrayItem('permitRequirements', index, { requiredFor: e.target.value })}
                        placeholder="Activity or area requiring permit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Issuing Authority</Label>
                      <Input
                        value={permit.issuingAuthority}
                        onChange={(e) => updateArrayItem('permitRequirements', index, { issuingAuthority: e.target.value })}
                        placeholder="Who issues this permit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={permit.status}
                        onValueChange={(v) => updateArrayItem('permitRequirements', index, { status: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">🟡 Pending</SelectItem>
                          <SelectItem value="Obtained">🟢 Obtained</SelectItem>
                          <SelectItem value="Not Required">⚪ Not Required</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
              ))}
              
              {formData.permitRequirements.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardCheck className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No permits added yet.</p>
                </div>
              )}
            </TabsContent>

            {/* Training Tab */}
            <TabsContent value="training" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Training Requirements</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => addArrayItem('trainingRequirements', {
                    trainingType: '',
                    requiredFor: '',
                    frequency: 'One-time',
                    verificationMethod: '',
                    rationale: ''
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Training
                </Button>
              </div>
              
              {formData.trainingRequirements.map((training, index) => (
                <Card key={rowKey(training, index, 'training')} className={`p-4 ${training.rationale ? 'border-l-4 border-l-purple-400' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {training.trainingType || `Training #${index + 1}`}
                      </Badge>
                      {training.rationale && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          AI
                        </Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeArrayItem('trainingRequirements', index)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  
                  {/* AI Rationale */}
                  {training.rationale && (
                    <div className="mb-3 p-2 bg-purple-50 rounded-md text-xs text-purple-800">
                      <span className="font-medium">💡 Why required:</span> {training.rationale}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Training Type</Label>
                      <Input
                        value={training.trainingType}
                        onChange={(e) => updateArrayItem('trainingRequirements', index, { trainingType: e.target.value })}
                        placeholder="e.g., Competent Person - Excavation"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Required For</Label>
                      <Input
                        value={training.requiredFor}
                        onChange={(e) => updateArrayItem('trainingRequirements', index, { requiredFor: e.target.value })}
                        placeholder="e.g., All workers, Ironworkers"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select
                        value={training.frequency}
                        onValueChange={(v) => updateArrayItem('trainingRequirements', index, { frequency: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="One-time">One-time</SelectItem>
                          <SelectItem value="Daily">Daily</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Verification Method</Label>
                      <Input
                        value={training.verificationMethod}
                        onChange={(e) => updateArrayItem('trainingRequirements', index, { verificationMethod: e.target.value })}
                        placeholder="e.g., Certificate, Card, Sign-off"
                      />
                    </div>
                    {training.linkedHazards?.length > 0 && (
                      <div className="md:col-span-2 text-xs text-gray-500">
                        <span className="font-medium">Linked to hazards:</span> {training.linkedHazards.join(', ')}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              
              {formData.trainingRequirements.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No training requirements added yet.</p>
                </div>
              )}
            </TabsContent>

            {/* PPE Tab */}
            <TabsContent value="ppe" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">PPE Requirements by Work Area</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => addArrayItem('ppeRequirements', {
                    workArea: '',
                    requiredPPE: [],
                    additionalRequirements: '',
                    rationale: ''
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Area
                </Button>
              </div>
              
              {formData.ppeRequirements.map((ppe, index) => (
                <Card key={rowKey(ppe, index, 'ppe')} className={`p-4 ${ppe.rationale ? 'border-l-4 border-l-purple-400' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-pink-50 text-pink-700">
                        <HardHat className="w-3 h-3 mr-1" />
                        {ppe.workArea || `Area #${index + 1}`}
                      </Badge>
                      {ppe.rationale && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          AI
                        </Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeArrayItem('ppeRequirements', index)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  
                  {/* AI Rationale */}
                  {ppe.rationale && (
                    <div className="mb-3 p-2 bg-purple-50 rounded-md text-xs text-purple-800">
                      <span className="font-medium">💡 Why required:</span> {ppe.rationale}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-2">
                      <Label>Work Area</Label>
                      <Input
                        value={ppe.workArea}
                        onChange={(e) => updateArrayItem('ppeRequirements', index, { workArea: e.target.value })}
                        placeholder="e.g., Excavation Zone, Welding Area"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Required PPE (comma-separated)</Label>
                      <Input
                        value={ppe.requiredPPE?.join(', ') || ''}
                        onChange={(e) => updateArrayItem('ppeRequirements', index, { 
                          requiredPPE: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        placeholder="Hard Hat, Safety Glasses, Steel-Toe Boots, Gloves"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Additional Requirements</Label>
                      <Input
                        value={ppe.additionalRequirements || ''}
                        onChange={(e) => updateArrayItem('ppeRequirements', index, { additionalRequirements: e.target.value })}
                        placeholder="Any special requirements"
                      />
                    </div>
                    {ppe.linkedHazards?.length > 0 && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Linked to hazards:</span> {ppe.linkedHazards.join(', ')}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              
              {formData.ppeRequirements.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <HardHat className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No PPE requirements added yet.</p>
                </div>
              )}
            </TabsContent>

            {/* Emergency Tab */}
            <TabsContent value="emergency" className="space-y-4 mt-4">
              <h3 className="font-medium">Emergency Action Plan</h3>
              
              <Card className="p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-red-600" />
                  Nearest Hospital
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Hospital Name</Label>
                    <Input
                      value={formData.emergencyPlan?.nearestHospital || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        emergencyPlan: { ...formData.emergencyPlan, nearestHospital: e.target.value }
                      })}
                      placeholder="Hospital name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={formData.emergencyPlan?.hospitalAddress || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        emergencyPlan: { ...formData.emergencyPlan, hospitalAddress: e.target.value }
                      })}
                      placeholder="Hospital address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.emergencyPlan?.hospitalPhone || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        emergencyPlan: { ...formData.emergencyPlan, hospitalPhone: e.target.value }
                      })}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <h4 className="font-medium mb-3">Evacuation Routes</h4>
                {(formData.emergencyPlan?.evacuationRoutes || ['']).map((route, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={route}
                      onChange={(e) => {
                        const newRoutes = [...(formData.emergencyPlan?.evacuationRoutes || [''])];
                        newRoutes[index] = e.target.value;
                        setFormData({
                          ...formData,
                          emergencyPlan: { ...formData.emergencyPlan, evacuationRoutes: newRoutes }
                        });
                      }}
                      placeholder={`Evacuation route ${index + 1}`}
                    />
                    {index > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newRoutes = formData.emergencyPlan.evacuationRoutes.filter((_, i) => i !== index);
                          setFormData({
                            ...formData,
                            emergencyPlan: { ...formData.emergencyPlan, evacuationRoutes: newRoutes }
                          });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({
                    ...formData,
                    emergencyPlan: {
                      ...formData.emergencyPlan,
                      evacuationRoutes: [...(formData.emergencyPlan?.evacuationRoutes || ['']), '']
                    }
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Route
                </Button>
              </Card>

              <Card className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Emergency Contacts</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({
                      ...formData,
                      emergencyPlan: {
                        ...formData.emergencyPlan,
                        emergencyContacts: [
                          ...(formData.emergencyPlan?.emergencyContacts || []),
                          { role: '', name: '', phone: '', email: '' }
                        ]
                      }
                    })}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Contact
                  </Button>
                </div>
                {(formData.emergencyPlan?.emergencyContacts || []).map((contact, index) => (
                  <div key={rowKey(contact, index, 'contact')} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 p-2 bg-gray-50 rounded">
                    <Input
                      value={contact.role}
                      onChange={(e) => {
                        const newContacts = [...formData.emergencyPlan.emergencyContacts];
                        newContacts[index] = { ...newContacts[index], role: e.target.value };
                        setFormData({
                          ...formData,
                          emergencyPlan: { ...formData.emergencyPlan, emergencyContacts: newContacts }
                        });
                      }}
                      placeholder="Role"
                    />
                    <Input
                      value={contact.name}
                      onChange={(e) => {
                        const newContacts = [...formData.emergencyPlan.emergencyContacts];
                        newContacts[index] = { ...newContacts[index], name: e.target.value };
                        setFormData({
                          ...formData,
                          emergencyPlan: { ...formData.emergencyPlan, emergencyContacts: newContacts }
                        });
                      }}
                      placeholder="Name"
                    />
                    <Input
                      value={contact.phone}
                      onChange={(e) => {
                        const newContacts = [...formData.emergencyPlan.emergencyContacts];
                        newContacts[index] = { ...newContacts[index], phone: e.target.value };
                        setFormData({
                          ...formData,
                          emergencyPlan: { ...formData.emergencyPlan, emergencyContacts: newContacts }
                        });
                      }}
                      placeholder="Phone"
                    />
                    <div className="flex gap-1">
                      <Input
                        value={contact.email || ''}
                        onChange={(e) => {
                          const newContacts = [...formData.emergencyPlan.emergencyContacts];
                          newContacts[index] = { ...newContacts[index], email: e.target.value };
                          setFormData({
                            ...formData,
                            emergencyPlan: { ...formData.emergencyPlan, emergencyContacts: newContacts }
                          });
                        }}
                        placeholder="Email"
                      />
                      {index > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newContacts = formData.emergencyPlan.emergencyContacts.filter((_, i) => i !== index);
                            setFormData({
                              ...formData,
                              emergencyPlan: { ...formData.emergencyPlan, emergencyContacts: newContacts }
                            });
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </Card>
            </TabsContent>

            {/* Subcontractors Tab */}
            <TabsContent value="subs" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Subcontractor Safety Requirements</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => addArrayItem('subcontractors', {
                    companyName: '',
                    scopeOfWork: '',
                    safetyContactName: '',
                    safetyContactPhone: '',
                    requiredDocuments: [],
                    documentsVerified: false,
                    notes: ''
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Subcontractor
                </Button>
              </div>
              
              {formData.subcontractors.map((sub, index) => (
                <Card key={rowKey(sub, index, 'sub')} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">Subcontractor #{index + 1}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => removeArrayItem('subcontractors', index)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input
                        value={sub.companyName}
                        onChange={(e) => updateArrayItem('subcontractors', index, { companyName: e.target.value })}
                        placeholder="Subcontractor company"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Scope of Work</Label>
                      <Input
                        value={sub.scopeOfWork}
                        onChange={(e) => updateArrayItem('subcontractors', index, { scopeOfWork: e.target.value })}
                        placeholder="What work they're doing"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Safety Contact Name</Label>
                      <Input
                        value={sub.safetyContactName}
                        onChange={(e) => updateArrayItem('subcontractors', index, { safetyContactName: e.target.value })}
                        placeholder="Contact name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Safety Contact Phone</Label>
                      <Input
                        value={sub.safetyContactPhone}
                        onChange={(e) => updateArrayItem('subcontractors', index, { safetyContactPhone: e.target.value })}
                        placeholder="(555) 555-5555"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Required Documents (comma-separated)</Label>
                      <Input
                        value={sub.requiredDocuments?.join(', ') || ''}
                        onChange={(e) => updateArrayItem('subcontractors', index, { 
                          requiredDocuments: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        placeholder="Insurance, Safety Plan, Training Records"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={sub.documentsVerified}
                          onChange={(e) => updateArrayItem('subcontractors', index, { documentsVerified: e.target.checked })}
                          className="rounded"
                        />
                        Documents Verified
                      </Label>
                    </div>
                  </div>
                </Card>
              ))}
              
              {formData.subcontractors.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No subcontractors added yet.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
              {selectedJHA ? 'Update JHA' : 'Create JHA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {selectedJHA?.projectName}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {selectedJHA && getStatusBadge(selectedJHA.status)}
              {selectedJHA?.projectNumber && (
                <span className="text-sm text-gray-500">#{selectedJHA.projectNumber}</span>
              )}
            </div>
          </DialogHeader>

          {selectedJHA && (
            <div className="space-y-4">
              {/* Project Info */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Project Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Location:</strong> {selectedJHA.projectLocation}</div>
                  <div><strong>Address:</strong> {selectedJHA.projectAddress}</div>
                  <div><strong>Client:</strong> {selectedJHA.clientName}</div>
                  <div><strong>GC:</strong> {selectedJHA.generalContractor}</div>
                  <div><strong>Start:</strong> {selectedJHA.estimatedStartDate}</div>
                  <div><strong>End:</strong> {selectedJHA.estimatedEndDate}</div>
                  <div className="col-span-2"><strong>Description:</strong> {selectedJHA.projectDescription}</div>
                  <div className="col-span-2"><strong>Scope:</strong> {selectedJHA.scopeOfWork}</div>
                </CardContent>
              </Card>

              {/* Hazards Summary */}
              {selectedJHA.siteHazards?.length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      Site Hazards ({selectedJHA.siteHazards.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedJHA.siteHazards.map((hazard, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <Badge variant="outline" className={
                            hazard.riskLevel === 'Critical' ? 'bg-red-100 text-red-700' :
                            hazard.riskLevel === 'High' ? 'bg-orange-100 text-orange-700' :
                            hazard.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }>{hazard.riskLevel}</Badge>
                          <span><strong>{hazard.hazardType}:</strong> {hazard.description}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* High Risk Activities */}
              {selectedJHA.highRiskActivities?.length > 0 && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-600" />
                      High-Risk Activities ({selectedJHA.highRiskActivities.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedJHA.highRiskActivities.map((activity, i) => (
                        <div key={i} className="text-sm p-2 bg-gray-50 rounded">
                          <strong>{activity.activity}</strong>
                          {activity.permitRequired && <Badge className="ml-2 bg-red-100 text-red-700">Permit Required</Badge>}
                          <p className="text-gray-600">{activity.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Rejection Reason */}
              {selectedJHA.status === 'rejected' && selectedJHA.rejectionReason && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-red-700">Rejection Reason</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-red-600">{selectedJHA.rejectionReason}</p>
                  </CardContent>
                </Card>
              )}

              {/* Approval Actions */}
              {selectedJHA.status === 'submitted' && (user?.role === 'company_admin' || user?.role === 'gc_admin' || user?.role === 'super_admin') && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-yellow-800">Approval Required</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openApproveDialog(selectedJHA)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="open-approve-signature-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve &amp; Sign
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => {
                          const reason = prompt('Enter rejection reason:');
                          if (reason) handleReject(selectedJHA.id, reason);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button onClick={() => selectedJHA && handleDownloadPDF(selectedJHA.id)}>
              <Download className="w-4 h-4 mr-1" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approver signature dialog — works with finger, stylus or mouse */}
      <Dialog open={showApproveDialog} onOpenChange={(open) => { setShowApproveDialog(open); if (!open) setApprovalSignature(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="approve-signature-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Approve &amp; Sign
            </DialogTitle>
            <DialogDescription>
              Your signature is recorded on the JHA and printed in the sign-off block of the PDF.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {emergencyBlocked && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-3" data-testid="approval-emergency-block">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-semibold">Emergency information required before approval</p>
                    <p>Missing: {(approvalReadiness?.emergencyPlanGaps || []).join(', ')}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => { setShowApproveDialog(false); handleEdit(selectedJHA); }}
                  data-testid="approval-complete-plan-btn"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Complete the emergency plan
                </Button>
                <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={useSitePlan}
                    onChange={(e) => setUseSitePlan(e.target.checked)}
                    data-testid="use-site-plan-checkbox"
                  />
                  <span>This work is covered by a site-wide emergency action plan</span>
                </label>

                {useSitePlan && (
                  <div className="space-y-2 border-t border-red-200 pt-3" data-testid="site-plan-form">
                    <Input
                      value={sitePlan.planName}
                      onChange={(e) => setSitePlan({ ...sitePlan, planName: e.target.value })}
                      placeholder="Plan name (e.g. Toledo Refinery Emergency Action Plan)"
                      data-testid="site-plan-name-input"
                    />
                    <Input
                      value={sitePlan.planRevision}
                      onChange={(e) => setSitePlan({ ...sitePlan, planRevision: e.target.value })}
                      placeholder="Plan revision or date (optional)"
                      data-testid="site-plan-revision-input"
                    />
                    <Input
                      value={sitePlan.accessLocation}
                      onChange={(e) => setSitePlan({ ...sitePlan, accessLocation: e.target.value })}
                      placeholder="Where crews can access it (link, attachment or onsite location)"
                      data-testid="site-plan-access-input"
                    />
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={sitePlan.coversThisWork}
                        onChange={(e) => setSitePlan({ ...sitePlan, coversThisWork: e.target.checked })}
                        data-testid="site-plan-covers-checkbox"
                      />
                      <span>I confirm this plan covers this scope of work</span>
                    </label>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={sitePlan.reviewedWithCrew}
                        onChange={(e) => setSitePlan({ ...sitePlan, reviewedWithCrew: e.target.checked })}
                        data-testid="site-plan-reviewed-checkbox"
                      />
                      <span>The relevant emergency procedures were reviewed with the crew</span>
                    </label>
                    <p className="text-xs text-gray-600">
                      Your signature below records this confirmation, your name and the time on the JHA and its PDF.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-sm">Signature</Label>
              <SignaturePad value={approvalSignature} onChange={setApprovalSignature} height={170} />
            </div>
            <div>
              <Label className="text-sm" htmlFor="approver-title">Title (printed under your name)</Label>
              <Input
                id="approver-title"
                value={approverTitle}
                onChange={(e) => setApproverTitle(e.target.value)}
                placeholder="e.g. Safety Director"
                data-testid="approver-title-input"
              />
            </div>
            <div>
              <Label className="text-sm" htmlFor="approval-comments">Comments (optional)</Label>
              <Textarea
                id="approval-comments"
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                rows={2}
                data-testid="approval-comments-input"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} data-testid="approve-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              disabled={!canApprove || approving}
              onClick={() => selectedJHA && handleApprove(selectedJHA.id)}
              data-testid="confirm-approve-btn"
            >
              {approving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              {approving ? 'Approving...' : 'Approve JHA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incomplete emergency plan warning before submitting for approval */}
      <Dialog open={!!submitWarning} onOpenChange={(open) => { if (!open) setSubmitWarning(null); }}>
        <DialogContent className="max-w-md" data-testid="emergency-plan-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Emergency plan incomplete
            </DialogTitle>
            <DialogDescription>
              This JHA is missing emergency information that crews rely on in an incident. It will print as
              "NOT COMPLETED" on the signed PDF.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-1.5" data-testid="emergency-plan-gap-list">
            {(submitWarning?.gaps || []).map((gap) => (
              <li key={gap} className="flex items-center gap-2 text-sm text-red-700">
                <XCircle className="w-4 h-4 shrink-0" />
                {gap}
              </li>
            ))}
          </ul>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => { const jha = submitWarning?.jha; setSubmitWarning(null); if (jha) handleEdit(jha); }}
              data-testid="complete-emergency-plan-btn"
            >
              <Edit className="w-4 h-4 mr-1" />
              Complete it now
            </Button>
            <Button
              className="bg-yellow-600 hover:bg-yellow-700"
              onClick={() => submitWarning?.jha && handleSubmit(submitWarning.jha.id, { skipWarning: true })}
              data-testid="submit-anyway-btn"
            >
              <Send className="w-4 h-4 mr-1" />
              Submit anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crew sign-on — supervisor collects acknowledgments on their device */}
      <Dialog open={showCrewDialog} onOpenChange={(open) => { setShowCrewDialog(open); if (!open) { setCrewTarget(null); setCrewSignature(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crew-signon-dialog">
          <DialogHeader>
            <DialogTitle>Crew Sign-On</DialogTitle>
            <DialogDescription>
              {crewData
                ? `Revision ${crewData.approvedRevision} · ${crewData.signedCount} of ${crewData.rosterCount} crew acknowledged`
                : 'Loading crew status...'}
            </DialogDescription>
          </DialogHeader>

          {crewData?.reacknowledgmentRequired && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-2.5" data-testid="reacknowledgment-banner">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                This JHA was revised. {crewData.previousRevisionSignOns?.length || 0} signature(s) stay on record against the
                previous revision, but every worker must review and sign revision {crewData.approvedRevision} again.
              </p>
            </div>
          )}

          {!crewTarget && crewData && (
            <div className="space-y-3">
              <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="crew-roster-list">
                {crewData.roster.map((worker) => (
                  <div key={worker.id} className="flex items-center justify-between gap-2 border rounded p-2">
                    <span className="text-sm truncate">{worker.name || worker.email}</span>
                    {worker.signed ? (
                      <Badge className="bg-green-100 text-green-700 border border-green-200 shrink-0">Signed</Badge>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setCrewTarget(worker)} data-testid={`crew-sign-${worker.id}`}>
                        Sign
                      </Button>
                    )}
                  </div>
                ))}
                {crewData.roster.length === 0 && (
                  <p className="text-sm text-gray-500">No workers on this project roster yet.</p>
                )}
              </div>

              <div className="border-t pt-3 space-y-2">
                <Label className="text-sm">Worker not on the list</Label>
                <div className="flex gap-2">
                  <Input
                    value={crewManualName}
                    onChange={(e) => setCrewManualName(e.target.value)}
                    placeholder="Full name"
                    data-testid="crew-manual-name-input"
                  />
                  <Button
                    variant="outline"
                    disabled={!crewManualName.trim()}
                    onClick={() => setCrewTarget({ id: null, name: crewManualName.trim() })}
                    data-testid="crew-manual-sign-btn"
                  >
                    Sign
                  </Button>
                </div>
              </div>

              {crewData.currentRevisionSignOns?.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-sm font-semibold text-gray-900 mb-1.5">
                    Acknowledged revision {crewData.approvedRevision}
                  </p>
                  <ul className="space-y-1 text-xs text-gray-600" data-testid="crew-signed-list">
                    {crewData.currentRevisionSignOns.map((s) => (
                      <li key={s.id} className="flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                        {s.workerName} · {(s.signedAt || '').replace('T', ' ').slice(0, 16)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {crewTarget && (
            <div className="space-y-3" data-testid="crew-signature-step">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{crewTarget.name || crewTarget.email}</span> confirms they reviewed
                revision {crewData?.approvedRevision} of this JHA before performing the work.
              </p>
              <SignaturePad value={crewSignature} onChange={setCrewSignature} height={170} />
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {crewTarget ? (
              <>
                <Button variant="outline" onClick={() => { setCrewTarget(null); setCrewSignature(null); }} data-testid="crew-back-btn">
                  Back
                </Button>
                <Button disabled={!crewSignature || crewSaving} onClick={handleCrewSignOn} data-testid="crew-save-signature-btn">
                  {crewSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                  {crewSaving ? 'Saving...' : 'Record signature'}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowCrewDialog(false)} data-testid="crew-close-btn">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SDS Linking Dialog */}
      <Dialog open={showSdsDialog} onOpenChange={setShowSdsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Link SDS Documents
            </DialogTitle>
            <DialogDescription>
              Link Safety Data Sheets to this JHA for chemical hazard awareness
            </DialogDescription>
          </DialogHeader>

          {sdsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2">Loading SDS suggestions...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Already Linked SDS */}
              {linkedSds.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2 text-green-700">Currently Linked ({linkedSds.length})</h4>
                  <div className="space-y-2">
                    {linkedSds.map((sds) => (
                      <div key={sds.id} className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium">{sds.chemical_name || sds.product_name}</span>
                        <span className="text-xs text-gray-500">- {sds.manufacturer}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested SDS */}
              {suggestedSds.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Suggested Based on Scope ({suggestedSds.length})</h4>
                  <div className="space-y-2">
                    {suggestedSds.map((sds) => (
                      <div 
                        key={sds.id} 
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          selectedSdsIds.has(sds.id) 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          const newSet = new Set(selectedSdsIds);
                          if (newSet.has(sds.id)) {
                            newSet.delete(sds.id);
                          } else {
                            newSet.add(sds.id);
                          }
                          setSelectedSdsIds(newSet);
                        }}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedSdsIds.has(sds.id)}
                          onChange={() => {}}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium">{sds.chemical_name || sds.product_name}</span>
                          <span className="text-xs text-gray-500 ml-2">- {sds.manufacturer}</span>
                          <Badge variant="outline" className="ml-2 text-xs">{sds.match_type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {suggestedSds.length === 0 && linkedSds.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No SDS documents found matching this project's scope.</p>
                  <p className="text-sm">Upload relevant SDS documents to the library first.</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSdsDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => selectedJHA && handleLinkSds(selectedJHA.id)}
              disabled={selectedSdsIds.size === 0}
            >
              <Link2 className="w-4 h-4 mr-1" />
              Link {selectedSdsIds.size} SDS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incidents Dialog */}
      <Dialog open={showIncidentsDialog} onOpenChange={setShowIncidentsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Related Incidents & Lessons Learned
            </DialogTitle>
            <DialogDescription>
              Review past incidents to inform hazard analysis for this project
            </DialogDescription>
          </DialogHeader>

          {incidentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
              <span className="ml-2">Loading incidents...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Linked Incidents */}
              {linkedIncidents.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Linked Incidents ({linkedIncidents.length})</h4>
                  <div className="space-y-2">
                    {linkedIncidents.map((inc, i) => (
                      <Card key={i} className="border-orange-200">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={
                              inc.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                              inc.severity === 'Serious' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>{inc.severity || 'Unknown'}</Badge>
                            <span className="font-medium text-sm">{inc.type || inc.incidentType}</span>
                          </div>
                          <p className="text-sm text-gray-600">{inc.description?.substring(0, 150)}...</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Location-based Incidents */}
              {locationIncidents.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2 text-blue-700">
                    Incidents at Similar Locations ({locationIncidents.length})
                  </h4>
                  <div className="space-y-2">
                    {locationIncidents.map((inc, i) => (
                      <Card key={i} className="border-blue-200">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={
                              inc.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                              inc.severity === 'Serious' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>{inc.severity || 'Unknown'}</Badge>
                            <span className="font-medium text-sm">{inc.incidentType}</span>
                            <span className="text-xs text-gray-400 ml-auto">{inc.location}</span>
                          </div>
                          <p className="text-sm text-gray-600">{inc.description?.substring(0, 150)}...</p>
                          {inc.aiAnalysis?.recommendations && (
                            <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                              <strong>AI Recommendation:</strong> {inc.aiAnalysis.recommendations[0]}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {linkedIncidents.length === 0 && locationIncidents.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                  <p>No incidents found related to this project.</p>
                  <p className="text-sm">This is good news - but always stay vigilant!</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncidentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreConstructionJHA;
